import { FormikHelpers, FormikValues } from 'formik';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { object, string } from 'yup';

import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Buscando from 'Component/Buscando';
import Cabecalho from 'Component/Cabecalho';
import Form, { Campo, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import LayoutDefault from 'Component/Layout';
import Paginacao from 'Component/Paginacao';
import SemRegistros from 'Component/SemRegistros';
import { UsuarioLista } from 'Component/Usuario';
import { useRecuperarUsuariosQuery } from 'Service';
import { useAppSelector } from 'Store';
import { VINCULOS_STATUS } from 'View/constants';
import { useRecuperarDepartamentosQuery } from 'View/Service/Default/Departamentos';
import { useRecuperarEmpresasQuery } from 'View/Service/Default/Empresas';
import { useRecuperarVinculosEmpregaticiosQuery } from 'View/Service/Default/VinculosEmpregaticios';

const regras = object({
  busca: string().optional().min(3, 'Deve ter mais que 3 letras'),
});

const Default = (): React.ReactElement => {
  const permissao = useAppSelector(
    (estado) => estado.autenticacao.usuario?.permissao
  );
  const navegacao = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (permissao && !['super', 'admin'].includes(permissao)) {
      navegacao('/inicio');
    }
  }, [navegacao, permissao]);

  const { data, refetch, isFetching, isLoading } = useRecuperarUsuariosQuery({
    contain: ['VinculosEmpregaticios.Empresas'],
    page: searchParams.get('pagina') || 1,
    options: {
      busca: { termo: searchParams.get('busca') || '' },
      status: searchParams.get('status') || '',
      empresa_id: searchParams.get('empresa_id') || '',
      departamento_id: searchParams.get('departamento_id') || '',
    },
    sort: 'Usuarios.nome',
    direction: 'asc',
  });

  const {
    data: { vinculos: [total] } = {
      vinculos: [{ clt: 0, estagio: 0 }],
    },
  } = useRecuperarVinculosEmpregaticiosQuery({
    finder: 'totalAtivos',
  });

  const { data: { empresas } = {} } = useRecuperarEmpresasQuery({
    finder: 'listaJson',
    limit: 50,
    sort: 'razao_social',
  });

  const { data: { departamentos } = {} } = useRecuperarDepartamentosQuery({
    finder: 'listaJson',
    limit: 50,
    sort: 'nome',
  });

  const filtrar = (
    dados: {
      busca: string;
      status: string;
      empresa_id: string;
      departamento_id: string;
    },
    form: FormikHelpers<FormikValues>
  ) => {
    if (dados.status == '1' || dados.status == '0') {
      searchParams.set('status', dados.status);
    } else {
      searchParams.delete('status');
    }
    if (dados.empresa_id) {
      searchParams.set('empresa_id', dados.empresa_id);
    } else {
      searchParams.delete('empresa_id');
    }
    if (dados.departamento_id) {
      searchParams.set('departamento_id', dados.departamento_id);
    } else {
      searchParams.delete('departamento_id');
    }
    searchParams.set('busca', dados.busca);
    searchParams.set('pagina', '1');

    setSearchParams(searchParams);

    form.setSubmitting(false);
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Funcionários"
        descricao="Listagem de funcionários cadastrados no banco de dados"
      />
      <Form
        emLinha
        onSubmit={filtrar}
        regras={regras}
        desabilitado={isFetching}
      >
        <div className="row">
          <div className="col-md-4 mb-3">
            <Botao icone="arrow-clockwise" onClick={refetch} className="me-2" />
            <BotaoLink
              tema="success"
              icone="plus-lg"
              to="/usuarios/adicionar"
              className="me-2"
            >
              Adicionar
            </BotaoLink>
            <a
              href="/usuarios/relatorio"
              className="btn btn-outline-primary"
              target="_blank"
            >
              <Icone nome="filetype-csv" className="me-2" />
              Relatório
            </a>
          </div>
          <div className="col-md-8 mb-3">
            <div className="hstack gap-3">
              <Selecao name="empresa_id" opcoes={empresas} padrao="Empresa" />
              <Selecao
                name="departamento_id"
                opcoes={departamentos}
                padrao="Departamento"
              />
              <Selecao name="status" opcoes={VINCULOS_STATUS} padrao="Status" />
              <Campo name="busca" rotulo="Digite o que deseja buscar" />
              <Botao type="submit" icone="filter" />
            </div>
          </div>
        </div>
      </Form>
      <Buscando exibir={isLoading} />
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="bg-white rounded shadow-sm p-3 mb-3 text-center">
            <h1>{total?.clt || 0}</h1>
            <span className="d-block">Ativos (CLT)</span>
          </div>
        </div>
        <div className="col-md-6">
          <div className="bg-white rounded shadow-sm p-3 mb-3 text-center">
            <h1>{total?.estagio || 0}</h1>
            <span className="d-block">Ativos (estágio)</span>
          </div>
        </div>
      </div>
      <SemRegistros
        carregando={isLoading}
        registros={data?.usuarios.length}
        redirecionar="/usuarios/adicionar"
      />
      <div className="bg-white shadow-sm rounded">
        {data?.usuarios?.map((usuario) => (
          <UsuarioLista
            key={`${usuario.id}-${usuario.modificado}`}
            dados={usuario}
          />
        ))}
      </div>
      <Paginacao dados={data?.paginacao} desabilitado={isFetching} />
    </LayoutDefault>
  );
};

export default Default;
